import * as React from "react";
import { useState } from "react";
import { Category } from "@/services/bedrock";
import { PaginationSimple } from "@/components/ui";
import { cn } from "@/lib/utils";
import { useSuperPageAssignmentSearch } from "@/utils/useSuperPageAssignmentSearch";
import type { GetSuperPageAssignmentsParams } from "@/services/bedrock/SuperPageServices";
import { SuperPageAssignmentsContent } from "./SuperPageAssignmentsContent";

type Props = {
  category: Category | null;
  searchTerm?: string;
  status?: "AVAILABLE" | "REQUESTED" | "COMPLETED";
  onAssignmentClick: (assignmentId: number) => void;
};

const SuperPageAssignmentsCategoryList: React.FC<Props> = ({ category, searchTerm, status, onAssignmentClick }) => {
  const [page, setPage] = useState(1);

  const listParams = React.useMemo((): GetSuperPageAssignmentsParams => {
    return {
      search: searchTerm?.trim() || undefined,
      status,
      categoryId: category?.id,
      uncategorized: category == null ? true : undefined,
      extraFields: {
        superPage: ["categories", "videoScript", "templateContentPage"],
        thumbnailUrl: true,
        contentPage: true,
      },
      page,
      perPage: 14,
      sort: "super_page_title",
      order: "asc",
    };
  }, [category, searchTerm, status, page]);

  const { superPageAssignments, isFetching } = useSuperPageAssignmentSearch(listParams);

  const content = superPageAssignments?.content ?? [];
  const totalPages = superPageAssignments?.pagination?.totalPages ?? 0;

  if (content.length === 0 && !isFetching) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <h2
          className={cn(
            "m-0 flex-grow p-0 text-start text-xl font-bold ignore-base-styling",
            !category ? "italic text-gray-500" : "text-foreground"
          )}
        >
          {category?.name ?? "Uncategorized"}
        </h2>
        {totalPages > 1 && (
          <PaginationSimple page={page} totalPages={totalPages} onPageChange={setPage} disabled={isFetching} />
        )}
      </div>
      <div className="flex flex-wrap gap-4">
        <SuperPageAssignmentsContent content={content} isFetching={isFetching} onAssignmentClick={onAssignmentClick} />
      </div>
    </div>
  );
};

export { SuperPageAssignmentsCategoryList };
